import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Таб-бар: Сегодня · Дневник · [+] · Тренды · Профиль.
 * Центральная кнопка — акцентный круг с «+»: одна кнопка ввода
 * на все, что человек записывает (концепция, раздел 08).
 */

const solid = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

/**
 * Иконки — плотный набор v2.1 из бренд-системы (brand-system/icons/tab-*.svg):
 * сплошные формы, как у Whoop. Одна форма на иконку, состояние задает только
 * цвет (.tab.active в global.css). Кат-ауты режутся цветом фона var(--bg-0).
 */
const cut = { stroke: 'var(--bg-0)' } as const;

function IconToday(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" {...solid}>
      <path d="M12 4.1A7.9 7.9 0 1 1 5.15 8.05" strokeWidth={3.6} />
    </svg>
  );
}

function IconDiary(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" {...solid}>
      <rect x="3.5" y="12.5" width="17" height="8" rx="3" fill="currentColor" stroke="none" />
      <rect x="5.5" y="7.75" width="13" height="3.25" rx="1.6" fill="currentColor" stroke="none" opacity={0.7} />
      <rect x="7.5" y="3.5" width="9" height="2.75" rx="1.4" fill="currentColor" stroke="none" opacity={0.45} />
    </svg>
  );
}

function IconTrends(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" {...solid}>
      <rect x="3.5" y="13" width="4.6" height="7.5" rx="2.3" fill="currentColor" stroke="none" />
      <rect x="9.7" y="8.5" width="4.6" height="12" rx="2.3" fill="currentColor" stroke="none" />
      <rect x="15.9" y="3.75" width="4.6" height="16.75" rx="2.3" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconProfile(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" {...solid}>
      <circle cx="12" cy="7.9" r="4.1" fill="currentColor" stroke="none" />
      <path d="M3.9 20.25a8.1 8.1 0 0 1 16.2 0z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Иконки разделов для шага 7 — активность и вес. */
export function IconActivity(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" {...solid}>
      <path d="M3.25 12.75h3.3l2.95-6.75 4.1 11.2 2.5-4.45h4.65" strokeWidth={3.3} />
    </svg>
  );
}

export function IconWeight(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" {...solid}>
      <rect x="3.5" y="3.5" width="17" height="17" rx="6" fill="currentColor" stroke="none" />
      <path d="M8.4 11.1a3.6 3.6 0 0 1 7.2 0" strokeWidth={1.9} style={cut} />
      <path d="M12 11.1l2.1-2.5" strokeWidth={1.9} style={cut} />
    </svg>
  );
}

export function IconHabits(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" {...solid}>
      <circle cx="12" cy="12" r="8.75" fill="currentColor" stroke="none" />
      <path d="M8.1 12.3l2.65 2.65 5.15-5.6" strokeWidth={2.1} style={cut} />
    </svg>
  );
}

function IconPlus(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" {...solid}>
      <path d="M12 5.25v13.5M5.25 12h13.5" strokeWidth={2.8} />
    </svg>
  );
}

interface TabDef {
  to: string;
  labelKey: string;
  icon: () => JSX.Element;
}

const LEFT_TABS: TabDef[] = [
  { to: '/', labelKey: 'tabs.today', icon: IconToday },
  { to: '/diary', labelKey: 'tabs.diary', icon: IconDiary },
];
const RIGHT_TABS: TabDef[] = [
  { to: '/trends', labelKey: 'tabs.trends', icon: IconTrends },
  { to: '/profile', labelKey: 'tabs.profile', icon: IconProfile },
];

function Tab({ def }: { def: TabDef }): JSX.Element {
  const { t } = useTranslation();
  const Icon = def.icon;
  return (
    <NavLink to={def.to} end className={({ isActive }) => (isActive ? 'tab active' : 'tab')}>
      <Icon />
      <span className="type-micro-11">{t(def.labelKey)}</span>
    </NavLink>
  );
}

export function TabBar(): JSX.Element {
  const { t } = useTranslation();
  return (
    <nav className="tabbar-wrap">
      <div className="tabbar">
        {LEFT_TABS.map((def) => (
          <Tab key={def.to} def={def} />
        ))}
        <NavLink to="/add" className="tab-add" aria-label={t('tabs.add')}>
          <IconPlus />
        </NavLink>
        {RIGHT_TABS.map((def) => (
          <Tab key={def.to} def={def} />
        ))}
      </div>
    </nav>
  );
}
